import { Link, NavLink, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider.jsx';
import { hasRole } from '../lib/roles.js';
import { useFeatures } from '../lib/useFeatures.js';
import { useConnectUnread } from '../lib/useStoreData.js';
import { FindOverlay } from './FindOverlay.jsx';

/**
 * SEVRIC Recon dock — v3.
 *
 *   [SEVRIC recon → Today]  Board · Units · Lot · Loads · Vendors   |   Find ⌘K · Connect (badge) · Admin
 *   portal · theme · avatar · sign out
 *
 * Lot = Battery Care baked into Recon. Loads = Shipping baked into Recon.
 * Flow (per-store stages) lives under Board → settings, not in the dock.
 */
export function TopBarV3() {
  const { profile, signOut } = useAuth();
  const isAdmin = hasRole(profile?.role, 'admin');
  const isManager = hasRole(profile?.role, 'service-manager');
  const features = useFeatures();
  const unread = useConnectUnread();
  const [findOpen, setFindOpen] = useState(false);
  const on = (k) => features[k] !== false;
  const { pathname } = useLocation();
  const underBoard = /^\/vehicle(\/|$)/.test(pathname);

  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setFindOpen(true); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => { applyStoredTheme(); }, []);

  const link = (to, label, Ico, end = false) => (
    <NavLink to={to} end={end} className={({ isActive }) => 'dock-link' + (isActive || (to === '/board' && underBoard) ? ' active' : '')}>
      {Ico && <Ico />}<span>{label}</span>
    </NavLink>
  );

  return (
    <>
      <div className="dock-wrap">
        <header className="dock">
          <Link to="/today" className={"dock-brand" + (pathname === "/today" || pathname === "/" ? " active" : "")} aria-label="SEVRIC Recon · Today" title="Today">
            <BrandMark />
            <div className="dock-brand-lockup"><span className="word">SEVRIC</span><span className="sub">recon</span></div>
          </Link>

          <nav className="dock-nav">
            {isManager && on('pipeline') && link('/board', 'Board', IcoBoard)}
            {isManager && link('/units', 'Units', IcoUnits)}
            {on('lot') && link('/lot', 'Lot', IcoLot)}
            {isManager && on('loads') && link('/loads', 'Loads', IcoLoads)}
            {isManager && on('vendors') && link('/vendors', 'Vendors', IcoVendors)}
            {!isManager && on('inspect') && link('/inspect', 'Inspect', IcoUnits)}
          </nav>

          <div className="dock-sep" />

          {on('find') && (
            <button type="button" className="dock-link" onClick={() => setFindOpen(true)} title="Find a vehicle, vendor, action (Cmd+K)">
              <IcoFind /><span>Find</span><span className="dock-kbd">⌘K</span>
            </button>
          )}
          {on('chat') && (
            <NavLink to="/connect" className={({ isActive }) => 'dock-link dock-connect' + (isActive ? ' active' : '')} title="SEVRIC Connect">
              <IcoMsg /><span>Connect</span>{unread > 0 && <span className="bd">{unread > 99 ? '99+' : unread}</span>}
            </NavLink>
          )}
          {isAdmin && (
            <NavLink to="/admin" className={({ isActive }) => 'dock-link' + (isActive ? ' active' : '')} title="Admin"><IcoAdmin /><span>Admin</span></NavLink>
          )}

          <div className="dock-sep" />

          <a href="https://portal.sevric.com" className="dock-tool" title="Portal" aria-label="Back to portal">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12l9-9 9 9" /><path d="M5 10v11h14V10" /></svg>
          </a>
          <button className="dock-theme" onClick={toggleTheme} title="Toggle light / dark" aria-label="Toggle theme" type="button"><IcoSun /><IcoMoon /></button>
          <div className="dock-avi" title={profile?.email || ''}>{initialsOf(profile)}</div>
          <button className="dock-signout" onClick={signOut} title="Sign out" aria-label="Sign out" type="button">⏻</button>
        </header>
      </div>
      <FindOverlay open={findOpen} onClose={() => setFindOpen(false)} />
    </>
  );
}

function BrandMark() {
  return (
    <svg className="dock-brand-mark" viewBox="0 0 240 240" xmlns="http://www.w3.org/2000/svg">
      <polygon points="200,120 160,189.28 80,189.28 40,120 80,50.72 160,50.72" fill="none" stroke="currentColor" strokeWidth="8" strokeLinejoin="miter" opacity="0.9" />
      <g className="inner"><polygon points="156,120 138,151.18 102,151.18 84,120 102,88.82 138,88.82" fill="currentColor" /></g>
    </svg>
  );
}
function IcoBoard() { return <svg className="dock-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="5" height="16" rx="1.5" /><rect x="10" y="4" width="5" height="11" rx="1.5" /><rect x="17" y="4" width="4" height="7" rx="1.5" /></svg>; }
function IcoUnits() { return <svg className="dock-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 14l2-5a2 2 0 0 1 1.9-1.3h10.2A2 2 0 0 1 19 9l2 5v4H3z" /><circle cx="7.5" cy="17" r="1.5" /><circle cx="16.5" cy="17" r="1.5" /><path d="M3 14h18" /></svg>; }
function IcoLot() { return <svg className="dock-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="18" height="10" rx="2" /><path d="M22 10v4" /><path d="M6 11v2M9 11v2M12 11v2" /></svg>; }
function IcoLoads() { return <svg className="dock-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M1 7h13v9H1z" /><path d="M14 10h4l4 4v2h-8z" /><circle cx="5" cy="18" r="1.8" /><circle cx="17" cy="18" r="1.8" /></svg>; }
function IcoVendors() { return <svg className="dock-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l1.5-4h15L21 9" /><path d="M3 9h18v11H3z" /><path d="M9 20v-6h6v6" /></svg>; }
function IcoMsg() { return <svg className="dock-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg>; }
function IcoFind() { return <svg className="dock-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>; }
function IcoAdmin() { return <svg className="dock-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 0 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1A1.7 1.7 0 0 0 4.6 9 1.7 1.7 0 0 0 4.3 7.2l-.1-.1a2 2 0 0 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></svg>; }
function IcoSun() { return <svg className="icon-sun" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" /></svg>; }
function IcoMoon() { return <svg className="icon-moon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>; }

function initialsOf(profile) {
  if (!profile) return '?';
  const name = profile.name || profile.email || '';
  const parts = name.split(/[ .]/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
function applyStoredTheme() {
  try { const saved = localStorage.getItem('sevric-theme'); if (saved === 'dark') document.body.classList.add('dark'); else document.body.classList.remove('dark'); } catch {}
}
function toggleTheme() {
  document.body.classList.toggle('dark');
  try { localStorage.setItem('sevric-theme', document.body.classList.contains('dark') ? 'dark' : 'light'); } catch {}
}
